import FocusTrap from 'focus-trap-react';
import React from 'react';
import { FormattedMessage } from 'react-intl';
import { v4 as uuid } from 'uuid';

import { Button } from '@xing-com/button';
import type { SocialTrackingMetadataInput } from '@xing-com/crate-common-graphql-types';
import { useTracking } from '@xing-com/crate-communication-tracking';
import type {
  OnErrorType,
  OnSuccessType,
} from '@xing-com/crate-social-common-share';
import {
  ShareSuccessBanner,
  DefaultAutohideErrorBanner,
  useSocialBanner,
  trackActionSheetOpen,
  QuickShare,
} from '@xing-com/crate-social-common-share';
import { useWrapRedirectLoggedOutUserOnClick } from '@xing-com/crate-social-utils';
import { IconMessages, IconNewChat, IconShare } from '@xing-com/icons';
import { Menu, SingleSelect, MenuHeadline } from '@xing-com/menu';
import { OmViewGridLess } from '@xing-com/platform-layout-om';
import { usePopOver } from '@xing-com/pop-over';

import { ShareViews } from '../../sections';

export type ShareActionSheetType = {
  backendTrackingMetaData?: SocialTrackingMetadataInput;
  buttonClassName?: string;
  entryPoint: string;
  hasQuickShare?: boolean;
  inputHintClick?: boolean;
  interactionTargetUrn: string;
  label?: React.ReactNode;
  onButtonClick?: () => void;
  onCancel?: () => void;
  onCountersUpdated?: (sharesCount: number) => void;
  onError?: OnErrorType;
  onSuccess?: () => void;
  propChannel: string;
  propSourceType?: string;
  url: string;
  verb?: 'SHARE' | 'POST';
  itemUrn?: string;
  trackingToken?: string | null;
};

export const ShareActionSheet: React.FC<ShareActionSheetType> = ({
  backendTrackingMetaData = { trackingTokens: [], page: '', position: '' },
  buttonClassName,
  entryPoint,
  hasQuickShare = false,
  inputHintClick = false,
  interactionTargetUrn,
  label,
  onButtonClick = () => undefined,
  onCancel = () => undefined,
  onCountersUpdated = () => undefined,
  onError = () => undefined,
  onSuccess: onSubmitSuccess = () => undefined,
  propChannel,
  propSourceType = '',
  url,
  verb = 'SHARE',
  itemUrn,
  trackingToken,
}) => {
  const propSocialInteractionId = React.useRef(uuid()).current;
  const { track } = useTracking();

  const [errorMessage, setErrorMessage] = React.useState('');
  const [selectedId, setSelectedId] = React.useState<'' | 'FEED' | 'MESSENGER'>(
    ''
  );

  const { handleHide, handleShow, triggerRef, show } = usePopOver();
  const { bannerProps, setBannerProps } = useSocialBanner();
  const { handleClick } = useWrapRedirectLoggedOutUserOnClick(() => {
    const { page, position, trackingTokens } = backendTrackingMetaData;
    onButtonClick();
    track({
      type: 'nwt',
      event: 'clicked',
      element: 'share',
      ...(position && { position: Number(position) }),
      itemUrn,
      trackingToken: trackingTokens?.[0] ?? undefined,
      sentBy: 'social',
      page: page?.toLowerCase(),
      application: 'wbm',
      eventSchema: 'extended',
    });
    trackActionSheetOpen({
      inputHintClick,
      interactionTargetUrn,
      propChannel,
      propSocialInteractionId,
    });
    handleShow();
  });

  const onSuccess: OnSuccessType = ({
    redirectUrl,
    targetType,
    interactionTarget,
  }) => {
    onCountersUpdated(interactionTarget?.sharesCount || 0);
    setBannerProps({ redirectUrl, show: true, targetType });
    onSubmitSuccess?.();
    handleHide();
  };

  return (
    <>
      <OmViewGridLess
        onClose={() => {
          triggerRef?.current?.focus();
          onCancel();
        }}
        preventPropagation
        trigger={(triggerOmView) => (
          <>
            <Button
              className={buttonClassName}
              data-testid="share-button"
              icon={IconShare}
              innerRef={triggerRef}
              onClick={handleClick}
            >
              {label}
            </Button>
            <Menu {...{ triggerRef, show }} onOutsideClick={handleHide}>
              <FocusTrap focusTrapOptions={{ clickOutsideDeactivates: true }}>
                <div data-qa="SHARE_ACTION_SHEET">
                  <MenuHeadline>
                    <FormattedMessage id="SHARE_ACTIONSHEET_HEADER" />
                  </MenuHeadline>
                  {hasQuickShare && (
                    <QuickShare
                      {...{ verb, onSuccess }}
                      onError={(error) => {
                        setBannerProps({ show: false });
                        setErrorMessage(error?.message || 'GENERIC_ERROR');
                        handleHide();
                      }}
                      trackingProps={{
                        backendTrackingMetaData,
                        trackingToken,
                        entryPoint,
                        inputHintClick,
                        interactionTargetUrn,
                        propChannel,
                        propSocialInteractionId,
                        propSourceType,
                      }}
                    />
                  )}
                  <SingleSelect
                    data-qa="SHARE_TO_STARTPAGE"
                    icon={IconNewChat}
                    onClick={(e) => {
                      setSelectedId('FEED');
                      setBannerProps({ show: false });
                      handleHide();
                      triggerOmView(e);
                    }}
                  >
                    <FormattedMessage id="SHARE_TO_STARTPAGE" />
                  </SingleSelect>
                  <SingleSelect
                    data-qa="SHARE_TO_MESSENGER"
                    icon={IconMessages}
                    onClick={(e) => {
                      setSelectedId('MESSENGER');
                      setBannerProps({ show: false });
                      handleHide();
                      triggerOmView(e);
                    }}
                  >
                    <FormattedMessage id="SHARE_TO_MESSENGER" />
                  </SingleSelect>
                </div>
              </FocusTrap>
            </Menu>
          </>
        )}
      >
        {({ handleClose }) => (
          <ShareViews
            openWithShareTo={selectedId}
            {...{
              backendTrackingMetaData,
              entryPoint,
              inputHintClick,
              interactionTargetUrn,
              onError,
              propChannel,
              propSocialInteractionId,
              propSourceType,
              url,
              verb,
            }}
            onCancel={() => {
              handleClose();
              onCancel();
            }}
            onSuccess={({ redirectUrl, targetType, interactionTarget }) => {
              onSuccess({ redirectUrl, targetType, interactionTarget });
              handleClose();
            }}
          />
        )}
      </OmViewGridLess>
      <ShareSuccessBanner {...{ ...bannerProps, setBannerProps }} />
      <DefaultAutohideErrorBanner {...{ show: !!errorMessage, errorMessage }} />
    </>
  );
};
